// services/database.service.js
import databaseClient, { handleDatabaseError } from '../api/databaseClient';

/**
 * Database Service — wrappers REST sobre el `database_service` Lambda.
 *
 * Convención (alineada con account/organization/billing services):
 *   Cada método devuelve `{ success, status, data | error }`.
 *   - `success: true` → `data` es el body parseado.
 *   - `success: false` → `error` es un objeto `{status, code, message, data}`
 *     producido por `handleDatabaseError`.
 *
 * Account scoping:
 *   Todas las rutas viven bajo `/accounts/{account_id}/...`. El backend
 *   también lee el `x-account-id` header (lo añade `databaseClient`), pero
 *   el path es la fuente de verdad.
 *
 * Database type:
 *   La mayoría de endpoints aceptan `?database_type=SQL|NOSQL` (default
 *   SQL). Lo pasamos siempre explícito para evitar ambigüedad.
 *
 * Este servicio expone métodos de **tablas**, **records**, **queries** y
 * **formularios de datos**.
 */

const buildQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const unwrap = (responseData) =>
  responseData?.body?.data ?? responseData?.body ?? responseData?.data ?? responseData;

const DatabaseService = {
  // ---------------------------------------------------------------
  // Tables
  // ---------------------------------------------------------------

  /**
   * GET /accounts/{accountId}/tables
   * Lista tablas para un account. Filtros opcionales: databaseType, isActive.
   * Paginación: `page` (1-indexed) y `pageSize` (max 100 server-side).
   * El response carga `tables`, `total_count`, `page`, `page_size`, `has_more`.
   *
   * Búsqueda y orden son **server-side** (grupo 01 del backend):
   *   - `search`: máx 100 chars, ILIKE sobre `name` y `description`.
   *   - `sortBy`: name | created_at | updated_at | row_count | size_bytes.
   *   - `sortDirection`: asc | desc (default del backend: updated_at desc).
   * No filtrar ni ordenar en cliente: con más de una página da resultados
   * incorrectos, que es justo el bug que arrastraba la versión anterior.
   *
   * `includeActivity` pide los contadores y sparklines de 7 días por fila
   * (`activity`), resueltos por el backend en una sola consulta agrupada.
   */
  async listTables(
    accountId,
    {
      databaseType,
      isActive,
      page,
      pageSize,
      search,
      sortBy,
      sortDirection,
      includeActivity,
    } = {},
    { signal } = {}
  ) {
    try {
      const qs = buildQuery({
        database_type: databaseType,
        is_active: isActive,
        page,
        page_size: pageSize,
        search,
        sort_by: sortBy,
        sort_direction: sortDirection,
        include_activity: includeActivity ? true : undefined,
      });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/database/overview?database_type=
   * Resumen del módulo para la cuenta: conteo de tablas, registros totales,
   * tamaño, última actualización, actividad del día/semana y uso de storage.
   *
   * Response (`AccountOverviewResponse`):
   *   `{ account_id, tables_count, total_records, total_size_bytes,
   *      last_updated_at,
   *      activity: { reads_today, writes_today, queries_today,
   *                  reads_7d, writes_7d },
   *      storage: { used_bytes, max_bytes, used_pct, breakdown } }`
   *
   * `storage.max_bytes` y `used_pct` pueden venir `null` (tier ilimitado);
   * `breakdown` todavía no se devuelve — depende de la decisión D-3 del
   * backend. La UI trata ambos casos.
   */
  async getDatabaseOverview(accountId, databaseType = 'SQL', { signal } = {}) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.get(
        `/accounts/${accountId}/database/overview${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/tables/{tableId}?database_type=
   * Trae una tabla con su schema completo.
   */
  async getTable(accountId, tableId, databaseType = 'SQL') {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}${qs}`
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/tables/{tableId}/schema/summary?database_type=SQL
   * Lectura derivada del schema para la pestaña Schema: conteos, distribución
   * de tipos, campos clave y el chequeo de "listo para agentes".
   *
   * Response (`SchemaSummaryResponse`):
   *   `{ table_id, columns_count, required_count, required_pct,
   *      distinct_types_count, type_distribution: [{type, count, pct}],
   *      primary_key, key_fields: [{name, role}],
   *      recommended_indexes: [name],
   *      agent_ready: { ready, reasons: [{code, columns}] } }`
   *
   * Todo lo calcula el backend con reglas fijas; el FE no rehace ninguna de
   * estas cuentas, para que la pantalla y cualquier otro consumidor no puedan
   * dar números distintos sobre el mismo schema.
   *
   * Sólo SQL: un schema NoSQL es una partition key más un mapa de atributos,
   * no una lista de columnas, y el backend responde 400 si se pide para uno.
   *
   * `role` y `code` son slugs estables, no texto para mostrar — el backend no
   * devuelve copy porque la app va en inglés y fijar la frase en el servicio
   * la ataría a un despliegue.
   */
  async getSchemaSummary(accountId, tableId, databaseType = 'SQL', { signal } = {}) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/schema/summary${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables?database_type=
   * Crea una tabla SQL o NoSQL.
   *
   * Body: { name, table_schema, settings?, is_active?, nosql_provider? }
   *
   * NoSQL responde con HTTP 202 y `status: "creating"`; el caller debe
   * hacer polling de `getTable` hasta que pase a `active` o `failed`.
   */
  async createTable(accountId, databaseType, body) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables${qs}`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * PUT /accounts/{accountId}/tables/{tableId}?database_type=
   * Actualiza metadatos de una tabla (name, settings, is_active).
   */
  async updateTable(accountId, tableId, databaseType, body) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.put(
        `/accounts/${accountId}/tables/${tableId}${qs}`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * PATCH /accounts/{accountId}/tables/{tableId}/schema?database_type=
   * Actualiza el schema de una tabla. Solo ADMIN.
   *
   * Body: { new_schema: {...} }
   */
  async updateTableSchema(accountId, tableId, databaseType, newSchema) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.patch(
        `/accounts/${accountId}/tables/${tableId}/schema${qs}`,
        { new_schema: newSchema }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * DELETE /accounts/{accountId}/tables/{tableId}?database_type=&cascade=
   * Borra una tabla. Solo ADMIN. `cascade=true` también borra registros.
   */
  async deleteTable(accountId, tableId, databaseType, { cascade = false } = {}) {
    try {
      const qs = buildQuery({ database_type: databaseType, cascade });
      const response = await databaseClient.delete(
        `/accounts/${accountId}/tables/${tableId}${qs}`
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Records
  // ---------------------------------------------------------------

  /**
   * GET /accounts/{accountId}/tables/{tableId}/records?database_type=&limit=&offset=&filters=&filter_logic=&sort_by=&sort_direction=
   * Paginación server-side: backend acepta `1 <= limit <= 1000` y `offset >= 0`.
   *
   * `filters` es un array `{column, operator, value?}` — se serializa a JSON
   * en un solo query param. Operadores válidos (SQL únicamente):
   * eq, neq, gt, gte, lt, lte, like, in, is_null, is_not_null. `is_null` /
   * `is_not_null` no llevan `value`. `filterLogic` es `"and"` o `"or"`
   * (default backend: `"and"`) y solo se envía si hay filtros.
   *
   * `sortBy` es el nombre de una columna real (`id`, `created_at`,
   * `updated_at`, `version`) o una key declarada en el schema de la tabla.
   * `sortDirection` es `"asc"` (default) o `"desc"` — siempre en minúscula
   * (el backend acepta cualquier casing, pero no hay razón para variarlo).
   * Solo aplica a tablas SQL: en NOSQL el backend ignora estos params en
   * silencio (DynamoDB no tiene ORDER BY nativo) — la UI ya evita mandarlos
   * para tablas NoSQL (ver `RecordsView`), pero si llegaran a mandarse no
   * rompen la request.
   *
   * `sortBy` inválido (no existe ni en el schema ni en el allowlist de
   * columnas reales) o `sortDirection` fuera de `asc`/`desc` → 400 con
   * `{ detail: { code: "VALIDATION_FAILED", message, request_id } }`
   * (ver `handleDatabaseError` en `api/databaseClient.js` para el parseo).
   *
   * Response: `{ records:[{record_id, data, version}], total_count, limit, offset }`.
   */
  async listRecords(
    accountId,
    tableId,
    databaseType,
    {
      limit = 50,
      offset = 0,
      filters,
      filterLogic,
      search,
      sortBy,
      sortDirection,
    } = {},
    { signal } = {}
  ) {
    try {
      const hasFilters = Array.isArray(filters) && filters.length > 0;
      const qs = buildQuery({
        database_type: databaseType,
        limit,
        offset,
        filters: hasFilters ? JSON.stringify(filters) : undefined,
        filter_logic: hasFilters ? filterLogic : undefined,
        // Búsqueda libre sobre el payload completo (máx 200 en el backend).
        // Se combina con `filters` mediante AND; `filter_logic` aplica solo
        // entre los filtros por columna.
        search: search || undefined,
        sort_by: sortBy || undefined,
        sort_direction: sortBy ? sortDirection : undefined,
      });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/records${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/tables/{tableId}/records/{recordId}?database_type=
   * Trae un record con su `data` y `version` (SQL).
   */
  async getRecord(accountId, tableId, recordId, databaseType) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/records/${recordId}${qs}`
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/records?database_type=
   * Body: `{ data: {...} }` — el `data` debe respetar el schema de la tabla.
   */
  async createRecord(accountId, tableId, databaseType, data) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/records${qs}`,
        { data }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * PUT /accounts/{accountId}/tables/{tableId}/records/{recordId}?database_type=
   * Body: `{ data, expected_version }`.
   *
   * **Crítico en SQL**: pasar `expectedVersion` con la versión actual del
   * record para que el backend rechace con 409 cuando otra escritura ganó
   * la carrera. Si se omite, el backend asume 1 y puede pisar cambios.
   */
  async updateRecord(
    accountId,
    tableId,
    recordId,
    databaseType,
    data,
    expectedVersion
  ) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const body = { data };
      if (typeof expectedVersion === "number") {
        body.expected_version = expectedVersion;
      }
      const response = await databaseClient.put(
        `/accounts/${accountId}/tables/${tableId}/records/${recordId}${qs}`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * DELETE /accounts/{accountId}/tables/{tableId}/records/{recordId}?database_type=&hard_delete=
   * `hard_delete=true` solo aplica en SQL — bypassea el recycle bin.
   */
  async deleteRecord(
    accountId,
    tableId,
    recordId,
    databaseType,
    { hardDelete = false } = {}
  ) {
    try {
      const qs = buildQuery({
        database_type: databaseType,
        hard_delete: hardDelete,
      });
      const response = await databaseClient.delete(
        `/accounts/${accountId}/tables/${tableId}/records/${recordId}${qs}`
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/records/batch?database_type=
   * Body: `{ records: [{data}, ...] }` — máx **100 por request**.
   * El caller (BatchImportDialog) chunkea si tiene más.
   *
   * Response: `{ created_count, failed_count, record_ids, errors, message }`.
   */
  async batchCreateRecords(accountId, tableId, databaseType, records) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/records/batch${qs}`,
        { records: records.map((r) => ({ data: r })) }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/records/batch-delete
   * Body: `{ record_ids, hard_delete }` — máx **100 por request**, mismo cap
   * que el batch de creación.
   *
   * Parcialmente tolerante: un id que no pertenece a la tabla se reporta en
   * `errors` y el resto igual se borra, así un id malo dentro de una
   * selección de cien no cuesta la operación entera.
   *
   * Response: `{ table_id, deleted_count, failed_count, errors, message }`.
   */
  async batchDeleteRecords(
    accountId,
    tableId,
    databaseType,
    recordIds,
    { hardDelete = false } = {}
  ) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/records/batch-delete${qs}`,
        { record_ids: recordIds, hard_delete: hardDelete }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/tables/{tableId}/activity?days=
   * Serie diaria de lecturas/escrituras/queries de la tabla, con los totales
   * de hoy y de ayer y la variación porcentual.
   *
   * `days`: 1..90 (default 7). La serie siempre trae `days` puntos, con ceros
   * en los días sin actividad, para que el sparkline no se deforme.
   * `delta_vs_yesterday` viene `null` cuando ayer fue 0 — ahí no se pinta el
   * delta, en vez de mostrar un "+∞%".
   */
  async getTableActivity(accountId, tableId, { days = 7 } = {}, { signal } = {}) {
    try {
      const qs = buildQuery({ days });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/activity${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/truncate?database_type=
   * Borra todos los registros y **conserva la tabla**: schema, id y la tabla
   * externa que lee Athena. Todo lo que apuntaba a ella —workflows,
   * asistentes— sigue apuntando bien.
   *
   * Body: `{ confirm_name }`. El nombre tiene que coincidir exacto,
   * mayúsculas incluidas, y lo valida **la API**, no solo el diálogo: la
   * operación no se deshace.
   *
   * Sólo ADMIN. 400 `VALIDATION_FAILED` si el nombre no coincide.
   */
  async truncateTable(accountId, tableId, databaseType, confirmName) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/truncate${qs}`,
        { confirm_name: confirmName }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Exports
  // ---------------------------------------------------------------

  /**
   * POST /accounts/{accountId}/tables/{tableId}/exports?database_type=
   *
   * Los campos de filtro son los mismos que `listRecords`, con la misma
   * semántica: el archivo trae lo que la pantalla muestra.
   *
   * Tres tramos, y el `status` de la respuesta dice en cuál cayó:
   *   - 200 `completed` + `download_url`  → hasta 1.000 filas, armado en el request.
   *   - 202 `processing`, `download_url: null` → hasta 10.000, lo arma el worker;
   *     se consulta con `getExport`.
   *   - 400 `EXPORT_LIMIT_EXCEEDED` → por encima del tope. El cuerpo trae
   *     `row_count` y `max_rows` para poder decir por cuánto se pasó.
   *
   * Con `dryRun` no produce archivo, no deja entrada en el historial y no
   * cobra —abrir el diálogo no es una operación—, pero **sí consume cuota**:
   * es un COUNT(*) con los filtros del cliente más una muestra serializada.
   * Devuelve `{ row_count, max_rows, within_limit, estimated_size_bytes, columns }`.
   */
  async createExport(accountId, tableId, databaseType, options = {}, { signal } = {}) {
    const {
      format = 'csv',
      filters,
      filterLogic,
      search,
      columns,
      sortBy,
      sortDirection,
      limit,
      dryRun = false,
    } = options;
    try {
      const qs = buildQuery({ database_type: databaseType });
      const body = { format };
      if (dryRun) body.dry_run = true;
      if (filters?.length) body.filters = filters;
      if (filterLogic) body.filter_logic = filterLogic;
      if (search) body.search = search;
      if (columns?.length) body.columns = columns;
      if (sortBy) body.sort_by = sortBy;
      if (sortDirection) body.sort_direction = sortDirection;
      if (limit != null) body.limit = limit;

      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/exports${qs}`,
        body,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/database/exports/{exportId}
   * Vuelve a firmar el enlace: el archivo dura una semana y la URL una hora.
   * `download_url` y `expires_at` vienen `null` cuando ya no hay nada que
   * enlazar (el export falló, o el archivo pasó sus 7 días).
   */
  async getExport(accountId, exportId, { signal } = {}) {
    try {
      const response = await databaseClient.get(
        `/accounts/${accountId}/database/exports/${exportId}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Imports
  // ---------------------------------------------------------------

  /**
   * POST /accounts/{accountId}/tables/{tableId}/imports/upload-url
   * Firma la subida. `size_bytes` por encima del tope se rechaza acá, antes
   * de transferir nada (400 `IMPORT_LIMIT_EXCEEDED`).
   *
   * Devuelve `{ import_id, upload_url, fields, expires_at }`. El formulario
   * vive 15 minutos.
   */
  async createImportUploadUrl(accountId, tableId, databaseType, file) {
    try {
      const qs = buildQuery({ database_type: databaseType });
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/imports/upload-url${qs}`,
        {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * Sube el archivo **directo a S3**, fuera de nuestro cliente HTTP.
   *
   * Es un POST con formulario, no un PUT: sólo el formulario lleva la
   * condición de tamaño. Los `fields` van tal cual llegaron y el archivo
   * último, que es como S3 espera el multipart. Sin headers de auth nuestros
   * —el formulario lleva su propia firma— y por eso usa `fetch` en vez de
   * `databaseClient`, que inyectaría el token y el `x-account-id`.
   */
  async uploadImportFile(uploadUrl, fields, file) {
    try {
      const form = new FormData();
      Object.entries(fields ?? {}).forEach(([key, value]) =>
        form.append(key, value)
      );
      form.append('file', file);

      const response = await fetch(uploadUrl, { method: 'POST', body: form });
      if (!response.ok) {
        return {
          success: false,
          error: {
            status: response.status,
            code: 'UPLOAD_FAILED',
            message: 'The file could not be uploaded. Please try again.',
          },
        };
      }
      return { success: true, status: response.status };
    } catch (error) {
      return {
        success: false,
        error: {
          status: 0,
          code: 'UPLOAD_FAILED',
          message: 'The file could not be uploaded. Please try again.',
        },
      };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/imports/{importId}/start
   *
   * Body: `{ format, has_header, delimiter, column_mapping, on_error }`.
   * `column_mapping` es **columna de la tabla → columna del archivo**.
   * `on_error`: `skip` importa lo válido y reporta el resto; `abort` es todo
   * o nada.
   *
   * No parsea nada: responde 202 y lo lee el worker. Valida lo que se puede
   * contestar sin abrir el archivo — que la subida exista, que haya cuota
   * (402) y que el import no se haya arrancado ya (400).
   */
  async startImport(accountId, tableId, importId, databaseType, options = {}) {
    const {
      format = 'csv',
      hasHeader = true,
      delimiter = ',',
      columnMapping,
      onError = 'skip',
    } = options;
    try {
      const qs = buildQuery({ database_type: databaseType });
      const body = { format, has_header: hasHeader, on_error: onError };
      if (format === 'csv') body.delimiter = delimiter;
      if (columnMapping && Object.keys(columnMapping).length > 0) {
        body.column_mapping = columnMapping;
      }
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/imports/${importId}/start${qs}`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/database/imports/{importId}
   * `status`: pending | processing | completed | failed. `pending` es el
   * estado entre firmar la subida y arrancarla — una subida abandonada no es
   * lo mismo que trabajo que no termina.
   *
   * `errors` trae como máximo los primeros 50; el resto está en el CSV de
   * `error_report_url`, con el número de fila del archivo original.
   */
  async getImport(accountId, importId, { signal } = {}) {
    try {
      const response = await databaseClient.get(
        `/accounts/${accountId}/database/imports/${importId}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Historial de operaciones
  // ---------------------------------------------------------------

  /**
   * GET /accounts/{accountId}/database/operations
   * Historial de la **cuenta**, filtrable por tabla. Registra create_table,
   * delete_table, schema_update, truncate, import y export — no el CRUD de
   * registros, que es actividad y va por `/activity`.
   *
   * `table_id` es nullable y `table_name` no: al borrar una tabla sus
   * entradas pierden el puntero y conservan el nombre, así que filtrar por
   * `tableId` deja de encontrarlas.
   */
  async listOperations(accountId, options = {}, { signal } = {}) {
    const { tableId, operationType, status, page = 1, pageSize = 5 } = options;
    try {
      const qs = buildQuery({
        table_id: tableId,
        operation_type: operationType,
        status,
        page,
        page_size: pageSize,
      });
      const response = await databaseClient.get(
        `/accounts/${accountId}/database/operations${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Queries (SQL)
  // ---------------------------------------------------------------

  /**
   * POST /accounts/{accountId}/tables/{tableId}/queries
   * Sólo SQL. NoSQL tiene su propio endpoint (`/nosql-queries`) que vive en Epic D.
   *
   * Body: `{ query_text, query_params? }`.
   *
   * Respuestas posibles (`SubmitQueryResponse`):
   *   - `status === "completed"` + `results: [...]`  → ejecutado sync o cache hit.
   *   - `status === "submitted"` + `query_job_id`    → ir a polling con `getQueryResults`.
   *
   * El backend decide sync vs async con una heurística naive: si la query
   * tiene `LIMIT N` con `N < 1000` → sync; en cualquier otro caso asume
   * 5000 rows → async. La mayoría de queries sin `LIMIT` van async.
   *
   * Errores notables:
   *   - 402 `MONTHLY_QUOTA_EXCEEDED` (no 429) — quota mensual agotada.
   *   - 400 — SQL inválido (mensaje en `error.message`).
   */
  async submitQuery(accountId, tableId, queryText, queryParams = undefined) {
    try {
      const body = { query_text: queryText };
      if (queryParams && Object.keys(queryParams).length > 0) {
        body.query_params = queryParams;
      }
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/queries`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Queries (NoSQL)
  // ---------------------------------------------------------------

  /**
   * POST /accounts/{accountId}/tables/{tableId}/nosql-queries
   * Solo NoSQL (DynamoDB). El endpoint es **síncrono** — la respuesta
   * trae los items directamente, sin polling. La paginación se hace con
   * `last_evaluated_key` (cursor opaco devuelto por DynamoDB).
   *
   * Body (`SubmitNoSQLQueryRequest`, ver Pydantic en el backend):
   *   - `query_type`: 'partition_key' | 'gsi' | 'scan'  (GSI diferida en v1)
   *   - `partition_key_value`: requerido para partition_key y gsi
   *   - `sort_key_condition`: `{ operator, value, value2? }`
   *     - operators: `=`, `<`, `<=`, `>`, `>=`, `begins_with`, `between`
   *   - `filter_expression`: `{ attribute, operator, value? }` (uno solo)
   *     - operators: `=`, `<`, `<=`, `>`, `>=`, `!=`, `begins_with`,
   *       `contains`, `exists`, `not_exists` (los últimos dos no llevan value)
   *   - `limit`: 1..1000 (default 100)
   *   - `last_evaluated_key`: pasarlo tal cual del response previo
   *   - `select_attributes`: lista de attrs a devolver (projection)
   *   - `scan_index_forward`: bool (asc/desc por sort key)
   *
   * Response (`SubmitNoSQLQueryResponse`):
   *   `{ items, count, scanned_count, last_evaluated_key,
   *      consumed_capacity, query_type, index_name, message }`.
   *   `consumed_capacity` puede ser `null` — la UI lo maneja.
   *
   * Errores notables:
   *   - 402 `MONTHLY_QUOTA_EXCEEDED` — quota mensual agotada.
   *   - 400 — request inválido (falta partition_key_value, operador
   *     malformado, attribute no existe, etc.).
   *   - 504 — API Gateway timeout (29s); ver §8 del plan de Epic D.
   */
  async submitNoSqlQuery(accountId, tableId, body, { signal } = {}) {
    try {
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/nosql-queries`,
        body,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/queries — con `query_spec`.
   *
   * El backend compila la spec server-side y ejecuta. Se manda uno u otro,
   * nunca ambos: `query_text` y `query_spec` juntos son 422.
   *
   * Respuesta idéntica a `submitQuery`: el discriminador es `status`, no la
   * presencia de `query_job_id` — una corrida síncrona también trae id, y es
   * el que después toma el export de resultados.
   */
  async submitQuerySpec(accountId, tableId, querySpec, queryParams = undefined) {
    try {
      const body = { query_spec: querySpec };
      if (queryParams && Object.keys(queryParams).length > 0) {
        body.query_params = queryParams;
      }
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/queries`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/queries/compile
   * Convierte la spec del constructor visual en SQL **sin ejecutarla**.
   *
   * El SQL lo genera el backend, siempre. El dialecto real es Athena/Presto y
   * el nombre físico de la tabla es interno (`dev_<cuenta>_<tabla>`); el
   * frontend no conoce ninguno de los dos y no arma SQL concatenando strings.
   *
   * Devuelve `{ sql, parameters[], summary[], warnings[], row_limit }`.
   * `parameters` son los `{{placeholders}}` que quedan por llenar y `summary`
   * son frases fijas por cláusula, sin LLM.
   *
   * Toda columna se valida contra el schema: una que no existe es 400
   * `VALIDATION_FAILED` **nombrándola**.
   */
  async compileQuery(accountId, tableId, querySpec, { signal } = {}) {
    try {
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/queries/compile`,
        querySpec,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/tables/{tableId}/query-templates
   * Catálogo fijo cruzado contra el schema: el mismo schema produce siempre
   * las mismas sugerencias en el mismo orden. Si ninguna aplica, lista vacía.
   *
   * Cada plantilla trae su `query_spec` ya armada. Se carga en el
   * constructor, no se ejecuta sola.
   */
  async listQueryTemplates(accountId, tableId, { signal } = {}) {
    try {
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/query-templates`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/tables/{tableId}/queries
   * Historial de ejecuciones de la tabla, sync y async por igual.
   *
   * Reemplaza al historial en `localStorage`, que se perdía al cambiar de
   * navegador y no se compartía con el equipo.
   */
  async listQueryHistory(accountId, tableId, options = {}, { signal } = {}) {
    const { page = 1, pageSize = 20, status } = options;
    try {
      const qs = buildQuery({ page, page_size: pageSize, status });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/queries${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  // ---------------------------------------------------------------
  // Consultas guardadas
  // ---------------------------------------------------------------

  /**
   * GET /accounts/{accountId}/tables/{tableId}/saved-queries
   * Compartidas a nivel de tabla: las ve todo el account y `created_by` queda
   * como auditoría. Ordena por `updated_at` descendente.
   */
  async listSavedQueries(accountId, tableId, options = {}, { signal } = {}) {
    const { page = 1, pageSize = 20 } = options;
    try {
      const qs = buildQuery({ page, page_size: pageSize });
      const response = await databaseClient.get(
        `/accounts/${accountId}/tables/${tableId}/saved-queries${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/tables/{tableId}/saved-queries
   * Hace falta `query_text` **o** `query_spec`; con ninguno, 422. Un campo
   * desconocido también es 422. Nombre repetido en la tabla → 409.
   */
  async createSavedQuery(accountId, tableId, body) {
    try {
      const response = await databaseClient.post(
        `/accounts/${accountId}/tables/${tableId}/saved-queries`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * DELETE /accounts/{accountId}/tables/{tableId}/saved-queries/{savedQueryId}
   * Una consulta guardada se direcciona a través de su tabla: pedir una que
   * vive en otra responde 404 aunque quien pregunta sea dueño de las dos.
   */
  async deleteSavedQuery(accountId, tableId, savedQueryId) {
    try {
      const response = await databaseClient.delete(
        `/accounts/${accountId}/tables/${tableId}/saved-queries/${savedQueryId}`
      );
      return { success: true, status: response.status };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/queries/{queryJobId}
   * Polling endpoint para queries async.
   *
   * Response (`GetQueryResultsResponse`):
   *   - `status ∈ "submitted" | "processing"` → seguir polleando.
   *   - `status === "completed"` + `results: [...]`   → inline (< 100KB).
   *   - `status === "completed"` + `results_url`       → presigned S3 (válido 1h).
   *   - `status === "failed"` + `error_message`         → mostrar al usuario.
   *
   * Nota: el `accountId` va sólo en path; el `query_job_id` ya contiene la
   * referencia a la tabla, así que no necesitamos repetir `tableId` aquí.
   */
  async getQueryResults(accountId, queryJobId) {
    try {
      const response = await databaseClient.get(
        `/accounts/${accountId}/queries/${queryJobId}`
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },
  // ---------------------------------------------------------------
  // Formularios de datos
  // ---------------------------------------------------------------
  //
  // Un form escribe en varias tablas de un solo envío, en una
  // transacción. Un campo es siempre una columna de una tabla: no hay
  // campos libres, y el backend rechaza cualquier llave que no sea un
  // campo declarado.
  //
  // Siete endpoints y dos audiencias. `getDataForm` devuelve la
  // definición cruda, que es lo que necesita un builder; `renderDataForm`
  // devuelve el formulario ya resuelto contra las tablas, que es lo que
  // necesita la pantalla que se llena. Pintar desde la definición cruda
  // obligaría a cruzar campo contra columna en JavaScript, que es la
  // misma regla que ya vive en Python — y las dos copias se separan.

  /**
   * POST /accounts/{accountId}/forms
   * Guarda una definición. **ADMIN**. Responde 201.
   *
   * `body` es `{ name, description?, is_active?, definition }`. Los
   * campos que decide el servidor se descartan si vienen, así que leer
   * un form, cambiarlo y mandarlo de vuelta funciona.
   *
   * Nombre repetido en la cuenta → 409. Una definición que no cuadra con
   * las tablas → 422 `FORM_DEFINITION_INVALID` con **todos** los
   * problemas en `detail.errors`, no de a uno por viaje.
   */
  async createDataForm(accountId, body) {
    try {
      const response = await databaseClient.post(
        `/accounts/${accountId}/forms`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/forms
   * Los formularios de la cuenta, el editado más recientemente primero.
   * **VIEWER**, y no cobra.
   *
   * Filtra por `isActive` y busca por `search` sobre el nombre (máx 100).
   * Pagina con `page` y `pageSize` (1–100, default 20). Como en tablas,
   * los tres son **server-side**: filtrar en memoria sobre la página
   * cargada responde "no hay resultados" para lo que nunca se pidió.
   *
   * Cada form trae `table_ids` derivado, sin repetir y en orden de
   * escritura, para que el listado diga qué tablas toca sin releer las
   * secciones.
   */
  async listDataForms(
    accountId,
    { isActive, search, page, pageSize } = {},
    { signal } = {}
  ) {
    try {
      const qs = buildQuery({
        is_active: isActive,
        search,
        page,
        page_size: pageSize,
      });
      const response = await databaseClient.get(
        `/accounts/${accountId}/forms${qs}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/forms/{formId}
   * La definición **cruda**. Es lo que necesita un builder.
   *
   * Para pintar el formulario está `renderDataForm`: esta no resuelve
   * nada contra las tablas, así que no sabe de qué tipo es una columna
   * ni qué opciones tiene un desplegable.
   */
  async getDataForm(accountId, formId, { signal } = {}) {
    try {
      const response = await databaseClient.get(
        `/accounts/${accountId}/forms/${formId}`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * PUT /accounts/{accountId}/forms/{formId}
   * **ADMIN**. Todos los campos son opcionales.
   *
   * `null` es una instrucción, no una omisión, y **solo `description` lo
   * acepta** para borrarla. Un `null` en `name`, `definition` o
   * `is_active` se rechaza: omitir el campo es como se deja igual, y
   * responder 200 sin cambiar nada dejaría al que llama creyendo que la
   * edición entró.
   */
  async updateDataForm(accountId, formId, body) {
    try {
      const response = await databaseClient.put(
        `/accounts/${accountId}/forms/${formId}`,
        body
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * DELETE /accounts/{accountId}/forms/{formId}
   * **ADMIN**. Devuelve `{ form_id, name, message }` — el nombre viene en
   * la respuesta para poder confirmarlo sin haberlo guardado antes.
   *
   * Borra el formulario, no lo que se escribió con él.
   */
  async deleteDataForm(accountId, formId) {
    try {
      const response = await databaseClient.delete(
        `/accounts/${accountId}/forms/${formId}`
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * GET /accounts/{accountId}/forms/{formId}/render
   * **El que usa el runner.** VIEWER, no cobra, y se puede repetir cada
   * vez que alguien reabre la pantalla.
   *
   * Devuelve el formulario resuelto contra las tablas **como están
   * ahora**: por cada campo, a qué columna escribe, de qué tipo es, si
   * hay que contestarlo y —para un desplegable— cuáles son las opciones.
   *
   * Dos llaves mandan sobre la pantalla:
   *   - `asked: false` → no se pide y **no se manda**. El backend lo
   *     produce al enviar y mandarlo es `FORM_UNKNOWN_FIELD`. Viaja igual
   *     para poder decir que la fila va a quedar completa.
   *   - `is_stale: true` → la definición ya no cuadra con las tablas.
   *     **Bloquea el envío**, y `errors[]` dice dónde.
   *
   * Un formulario envejecido se renderiza igual, al revés que el envío
   * que lo rechaza: la pantalla necesita algo que mostrar mientras dice
   * qué está mal. El único caso que vuelve sin secciones es una
   * definición que ni siquiera parsea — no hay nada de qué construirlas.
   */
  async renderDataForm(accountId, formId, { signal } = {}) {
    try {
      const response = await databaseClient.get(
        `/accounts/${accountId}/forms/${formId}/render`,
        { signal }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },

  /**
   * POST /accounts/{accountId}/forms/{formId}/submissions
   * **MEMBER**. Cobra **un** write, no uno por registro. Responde 201.
   *
   * `sections` va keyed por sección: una repetible manda **lista**, una
   * normal manda **objeto**. Solo van los campos con `asked: true`.
   *
   * Y solo los que tienen respuesta: una llave presente con `""` se
   * escribe como cadena vacía **y anula el `default` del campo**, porque
   * el default solo corre cuando el valor falta o es `null`. Omitir es
   * lo que deja la columna ausente, que es distinto de vacía.
   *
   * Nada se escribe hasta que todo pasa, y lo que se escribe va en una
   * sola transacción: media reserva no es un estado que el bot ni los
   * workflows sepan interpretar.
   *
   * La respuesta trae `generated` por sección de una fila — el id que se
   * le asignó a la reserva se lee ahí, sin ir a buscarlo a la tabla.
   */
  async submitDataForm(accountId, formId, sections) {
    try {
      const response = await databaseClient.post(
        `/accounts/${accountId}/forms/${formId}/submissions`,
        { sections }
      );
      return {
        success: true,
        status: response.status,
        data: unwrap(response.data),
      };
    } catch (error) {
      return { success: false, error: handleDatabaseError(error) };
    }
  },
};

export default DatabaseService;
